import { SelectedSupplier, PartQuantity } from "./partsCalculationUtils";
import { CPTSlab } from "./cptSlabUtils";

const isValidDistance = (distanceKm: number | undefined): boolean => {
  return (
    distanceKm !== undefined &&
    distanceKm !== null &&
    !isNaN(distanceKm) &&
    distanceKm !== Infinity
  );
};

const createOrderItemsMap = (orderItems: any[]): Map<string, any> => {
  const map = new Map<string, any>();
  orderItems.forEach((item) => {
    if (item.rootPartNumber) {
      map.set(item.rootPartNumber, item);
    }
  });
  return map;
};

const calculateActualFulfillingQuantity = (
  selectedPart: { rootPartNumber: string; familyQuantity: number },
  orderItem: any,
  previousSuppliers: SelectedSupplier[],
  originalParts: PartQuantity[]
): number => {
  const originalQuantity =
    originalParts.find((p) => p.rootPartNumber === selectedPart.rootPartNumber)
      ?.quantity ??
    orderItem?.remainingDecisionQuantity ??
    0;

  let alreadyFulfilled = 0;
  for (const supplier of previousSuppliers) {
    const part = supplier.selectedParts.find(
      (p) => p.rootPartNumber === selectedPart.rootPartNumber
    );
    if (part) {
      alreadyFulfilled += part.familyQuantity;
    }
  }

  const remainingNeeded = Math.max(0, originalQuantity - alreadyFulfilled);
  return Math.min(selectedPart.familyQuantity, remainingNeeded);
};

export const calculateSupplierPrice = (
  orderItems: any[],
  selectedSupplier: SelectedSupplier,
  allSelectedSuppliers: SelectedSupplier[],
  originalParts: PartQuantity[]
): number | null => {
  if (!orderItems?.length || !selectedSupplier.selectedParts?.length) {
    return null;
  }

  const orderItemsMap = createOrderItemsMap(orderItems);
  const previousSuppliers = allSelectedSuppliers.filter(
    (s) => s.selectedAt < selectedSupplier.selectedAt
  );

  let totalPrice = 0;
  let hasValidMRP = false;

  for (const selectedPart of selectedSupplier.selectedParts) {
    const orderItem = orderItemsMap.get(selectedPart.rootPartNumber);

    if (orderItem?.mrp > 0) {
      const fulfillingQuantity = calculateActualFulfillingQuantity(
        selectedPart,
        orderItem,
        previousSuppliers,
        originalParts
      );

      if (fulfillingQuantity > 0) {
        totalPrice += orderItem.mrp * fulfillingQuantity;
        hasValidMRP = true;
      }
    }
  }

  return hasValidMRP ? totalPrice : null;
};

export const calculateDeliveryFee = (
  distanceKm: number | undefined,
  perKmPrice: number,
  minDeliveryFee: number,
  cptSlab?: CPTSlab
): number | null => {
  if (cptSlab !== CPTSlab.HLD) {
    return null;
  }

  if (!isValidDistance(distanceKm)) {
    return null;
  }

  const calculatedFee = distanceKm! * perKmPrice;
  return Math.max(calculatedFee, minDeliveryFee);
};
